<template>
  <section>
    <SubLinks />
    <div
      class="inline-flex items-center space-x-3 cursor-pointer w-auto"
      @click="goToShowList"
      id="all-shows-link"
    >
      <ResArrowLeft class="scale-125" />
      <span class="text-fg-link-primary">All Shows</span>
    </div>

    <div
      class="flex flex-col space-y-10 mt-2 md:flex-row md:space-y-0"
      ref="content"
      v-if="currentShow"
    >
      <!--Show info card-->
      <div
        class="details border-b pb-8 space-y-3 w-full md:border-r border-linear-strong md:pr-8 md:w-96 md:border-b-0 md:grow-[20]"
      >
        <h1 class="text-3xl font-heading font-bold" id="title-text">
          {{ currentShow.name }}
        </h1>
        <img
          :src="currentShow.srcset.original.src"
          :alt="currentShow.name"
          class="rounded w-full"
        />
        <p class="font-sans mt-3">
          {{ currentShow.description }}
        </p>
        <ErrorDisplay v-bind="ui.error" />
        <div class="flex items-center justify-between">
          <ButtonDefault label="Edit" @click="onEditShow" id="edit-show-btn" alt />
          <ButtonDefault label="Duplicate" @click="onDuplicateShow" id="duplicate-show-btn" alt />
          <ButtonDefault
            v-if="canDeleteShow"
            label="Delete"
            @click="deleteShowDialog = true"
            id="delete-show"
            alt
          />
        </div>
      </div>

      <!--Performance list-->
      <div class="w-full md:ml-4 md:shrink-[20] lg:ml-8">
        <div class="flex flex-col xs:items-baseline xs:flex-row">
          <span class="text-lg font-heading font-bold">Performances</span>
          <span
            v-for="timeFilter in performanceTimeFilters"
            :key="timeFilter"
            class="cursor-pointer font-sans xs:ml-8 xs:mr-2 capitalize"
            :class="performanceTimeFilter === timeFilter ? 'font-bold' : 'text-fg-link-primary'"
            @click="switchPerformanceFilter(timeFilter)"
          >
            {{ timeFilter }}
          </span>
          <ButtonDefault
            @click="onAddPerformance()"
            label="Add"
            classes="mr-auto mb-2 xs:mb-0 xs:ml-auto xs:mr-0"
          />
        </div>

        <div class="overflow-x-auto relative w-full">
          <table class="w-full min-w-500px table-fixed border-separate border-spacing-3">
            <thead class="border-b border-linear-strong">
              <tr>
                <th
                  :key="header"
                  class="py-1 text-uppercase text-sm font-label font-weight-regular text-left"
                  scope="col"
                  v-for="header in ['Date', 'Show Time']"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan="5"><hr class="h-1 w-full border-linear-strong" /></td>
              </tr>
              <tr
                :id="'performance-' + p.id"
                :key="p.id"
                @click="onEditPerformance(p.id)"
                class="cursor-pointer performance"
                v-for="p in performancesToDisplay"
              >
                <td class="font-sans text-fg-link-primary">{{ p.performance }}</td>
                <td class="font-sans text-fg-link-primary">{{ p.time }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <span
          @click="loadMorePerformances"
          class="mt-4 text-fg-link-primary font-sans cursor-pointer"
          id="load-more-btn"
          v-show="performancesToDisplay.length < totalOfPerformances"
        >
          Load More
        </span>
      </div>
    </div>

    <SlotModal
      :display="performanceModal.visible"
      maxWidth="500px"
      v-if="currentShow"
      @close="performanceModal.visible = false"
    >
      <PerformanceForm :show="currentShow" @save="savePerformance" />
    </SlotModal>

    <SlotModal :display="deleteShowDialog" @close="deleteShowDialog = false">
      <template #header>Delete Show</template>
      <template #default>
        <div class="font-sans text-base">
          Are you sure you want to delete show
          <span class="font-semibold" v-if="currentShow">{{ currentShow.name }}</span
          >?
        </div>
        <div class="flex justify-between mt-8">
          <ButtonDefault
            label="Yes"
            id="delete-show-confirm"
            class="text-white"
            @click="deleteShow"
          />
          <ButtonDefault
            label="Cancel"
            id="delete-show-dialog-cancel"
            @click="deleteShowDialog = false"
            alt
          />
        </div>
      </template>
    </SlotModal>
  </section>
</template>

<style scoped>
.description-edit /deep/ textarea {
  font-size: 15px;
  line-height: 22.5px;
}
</style>

<style lang="scss" scoped>
.show-image {
  position: relative;
  img {
    width: 100%;
  }

  &:not(.editing)::after {
    position: absolute;
    content: '';
    width: 100%;
    height: 100%;
    opacity: 0;
    left: 0px;
    top: 0px;
  }
}

.cropper {
  width: 100%;
}
</style>

<script lang="ts">
import debounce from 'debounce-promise'
import moment from 'moment'
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ResArrowLeft from '@/assets/resArrowLeft.svg?component'
import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import SubLinks from '@/common/components/molecules/SubLinks.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'
import PerformanceForm from '@/components/PerformanceForm.vue'
import { StoreFloorPlan, StorePerformance, StoreShow } from '@/models'
import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useFloorPlanStore } from '@/stores/floorplan'
import { useSettingStore } from '@/stores/setting'
import { useShowStore } from '@/stores/show'
import { blankError, displayHandled } from '@/utils/errors'
import { sortBy } from '@/utils/utils'

import { PerformanceCreate } from '@generated/types'

type PerformanceTimeFilter = 'future' | 'past'

export default defineComponent({
  name: 'ShowDetail',
  components: {
    ButtonDefault,
    ErrorDisplay,
    PerformanceForm,
    ResArrowLeft,
    SlotModal,
    SubLinks,
  },
  data() {
    return {
      performanceModal: {
        visible: false,
        date: '',
        time: '',
        arrangement: null,
        id: 0,
      },
      performancePage: 1,
      performanceLimit: 20,
      deleteShowDialog: false,
      updateShowMetaData: (_: any) => {
        _
        return
      },
      tableViewWidth: 100,
      tableIndex: -1,
      performanceTimeFilter: 'future' as PerformanceTimeFilter,
      performanceTimeFilters: ['future', 'past'] as PerformanceTimeFilter[],
      ui: {
        error: blankError(),
      },
      isShowBeingDeleting: false,
    }
  },

  async mounted() {
    await Promise.all([
      this.floorPlanStore.fetchAll(),
      this.performanceStore.getPerformances({ pagination: 'false' }),
      this.showStore.fetchShows(),
    ])

    this.updateShowMetaData = debounce(show => {
      this.showStore.upsertShow(show)
    }, 200)

    this.onResize = this.onResize.bind(this)
    window.addEventListener('resize', this.onResize)
    this.$nextTick(this.onResize)
  },

  computed: {
    ...mapStores(useSettingStore, useShowStore, useFloorPlanStore, usePerformanceStore),

    shows(): StoreShow[] {
      return this.showStore.shows
    },

    showId(): number {
      if (this.$route.params.id) {
        return Number(this.$route.params.id)
      }
      return 0
    },

    currentShow(): StoreShow | undefined {
      if (this.showId < 1 || this.shows.length === 0 || this.isShowBeingDeleting) {
        return
      }
      return this.showStore.showById(this.showId)
    },

    arrangements(): StoreFloorPlan[] {
      return this.floorPlanStore.arrangements
    },

    performances(): (StorePerformance & { timeFilter: PerformanceTimeFilter })[] {
      const performances: (StorePerformance & { timeFilter: PerformanceTimeFilter })[] = sortBy(
        this.performanceStore.performances
          .filter(p => p.show_id === this.showId)
          .map(p => ({
            ...p,
            timeFilter: this.settingStore.parseMoment(p.datetime) > moment() ? 'future' : 'past',
          })),
        'datetime',
      )
      if (this.performanceTimeFilter === 'past') {
        return performances.reverse()
      }
      return performances
    },
    performancesToDisplay(): any[] {
      return this.performances
        .filter(p => p.timeFilter === this.performanceTimeFilter)
        .map(p => ({
          id: p.id,
          date: p.date,
          time: this.settingStore.timeStr(p.datetime),
          performance: this.settingStore.formatTemporal(p.datetime, 'MMMM DD, YYYY'),
          tables: p.tables,
        }))
        .slice(0, this.performancePage * this.performanceLimit)
    },
    totalOfPerformances(): number {
      return this.performances.filter(p => p.timeFilter === this.performanceTimeFilter).length
    },
    canDeleteShow(): boolean {
      return this.performances.length === 0
    },
  },
  methods: {
    goToShowList() {
      return this.$router.push({ name: 'showList' })
    },
    onResize() {
      if (this.$refs.content) {
        const dimensions = (this.$refs.content as Element).getBoundingClientRect()
        this.tableViewWidth = dimensions.width * 0.7
      }
    },
    onEditShow() {
      this.$router.push({ name: 'showEdit', params: { id: this.showId.toString() } })
    },
    async onDuplicateShow() {
      try {
        const newShow = await this.showStore.duplicateShow(this.showId)
        await this.$router.push({ name: 'showEdit', params: { id: newShow.id.toString() } })
      } catch (e) {
        displayHandled(e, this.ui.error)
      }
    },
    async onEditPerformance(performanceId) {
      await this.$router.push({
        name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST,
        params: { performanceId },
      })
    },
    async onAddPerformance() {
      await this.$router.push({ name: 'performanceAdd', params: { showId: `${this.showId}` } })
    },
    savePerformance(perf: PerformanceCreate) {
      this.performanceStore.createPerformance(perf)

      this.performanceModal.visible = false
    },
    async deleteShow() {
      if (!this.canDeleteShow) return
      this.isShowBeingDeleting = true
      const id = this.showId
      await this.showStore.deleteShow(id)
      this.deleteShowDialog = false
      await this.goToShowList()
    },
    loadMorePerformances() {
      this.performancePage += 1
    },
    switchPerformanceFilter(value: PerformanceTimeFilter) {
      this.performanceTimeFilter = value
      this.performancePage = 1
    },
  },
})
</script>
